import React, { useState, useMemo, useEffect} from 'react'
import {
  Button,
  Grid,
  Modal,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  FormControlLabel, Switch,
  Divider
} from '@mui/material'
import { RemovalBroker } from '../../../redux/RemovalBrokersTypes/RemovalBrokersTypes';
interface ConfirgureRemovalBrokersProps {
  open: boolean
  setOpen: (open: boolean) => void
  broker: RemovalBroker
  users : Record<string, any>[]
  configUpdate: (request: any) => Promise<void>
}

const removalRolesOptions = [
    "N/A",
    "Removal Manager",
    "Removal Specialist"
]

const removalRoleMapping = {
  "Removal Manager": "removal_manager",
  "Removal Specialist": "removal_specialist",
  "N/A" : "N/A"
}

const ConfirgureRemovalBrokersModal = (props: ConfirgureRemovalBrokersProps) => {
    const [removalGroup, setRemovalGroup] = useState<string | null>(null)
    const [removalAssigneeList, setRemovalAssigneeList] = useState<string[] | null>(null)
    const [assignToAll, setAssignToAll] = useState<boolean>(false)
    const [hasChanges, setHasChanges]= useState(false)
    const [holdBrokerStatus, setHoldBrokerStatus] = useState(false);

    useEffect(()=>{
        setRemovalGroup(props.broker.removal_group)
        setRemovalAssigneeList(props.broker.removal_assignees)
        setAssignToAll(false)
        setHoldBrokerStatus(!!props.broker.is_removal_on_hold)
    },[props.broker, props.open])

    const getRemovalRoleKeyFromValue = (value: string) =>
        Object.keys(removalRoleMapping).find(
        key => removalRoleMapping[key as keyof typeof removalRoleMapping] === value
    )

        const assigneeOptions = useMemo(() => {
        if (!props.users || props.users.length === 0) return [];

        return props.users
            .filter((user) => {
                if (!removalGroup) return false;

                const role = user.Attributes?.["custom:removal_role"];

                if (removalGroup === "N/A") {
                    return !role;
                }
                
                return role === removalGroup;
            })
            .map((user) => user.Username);
    }, [props.users, removalGroup]);


    useEffect(() => {
        const originalGroup = props.broker.removal_group ?? null
        const originalAssignees = props.broker.removal_assignees ?? []
        const originalHoldStatus = props.broker.is_removal_on_hold ?? false

        const normalize = (arr: string[] | null) =>
            [...(arr ?? [])].sort()

        const groupChanged = originalGroup !== removalGroup
        const holdChanged = originalHoldStatus !== holdBrokerStatus

        let assigneesChanged = false

        if (assignToAll) {
            assigneesChanged =
                JSON.stringify(normalize(originalAssignees)) !==
                JSON.stringify(normalize(assigneeOptions))
        } else {
            assigneesChanged =
                JSON.stringify(normalize(originalAssignees)) !==
                JSON.stringify(normalize(removalAssigneeList))
        }

        const baseChanges = groupChanged || assigneesChanged || holdChanged;

        const shouldDisableChanges =
            removalGroup &&
            removalGroup !== "N/A" &&
            !assignToAll &&
            !removalAssigneeList;

        setHasChanges(baseChanges && !shouldDisableChanges);
    }, [
        props.broker,
        removalGroup,
        removalAssigneeList,
        assignToAll,
        assigneeOptions, 
        holdBrokerStatus
    ])

    const onClose=()=>{
        props.setOpen(false)
        setRemovalGroup(null)
        setRemovalAssigneeList(null)
        setAssignToAll(false)
    }
    
    const modalStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80vw',
        maxWidth: 1000,
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column' as 'column',
        bgcolor: 'background.paper',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        borderRadius: '16px',
        p: 15,
        zIndex: 9999,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
    }

  return (
    <Modal
      open={props.open}
      sx={{
        zIndex: 9999,
          backdropFilter: 'blur(4px)',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        width : '100%'
      }}>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{...modalStyle}}>
          <Typography 
            variant="h2"
            mt={{xs: 2, sm: 2, md: 3, lg: 3}} 
            textAlign="center"
            sx={{
              fontSize: {xs: 18, md: 21, lg: 23},
              fontWeight: 600,
              lineHeight: {xs: 1, md: 1.2, lg: 1.5},
            }}>
            Configure Broker
          </Typography>
        <Grid container spacing={4} mb={10} mt={5} width={'100%'}>
            <Grid item lg={6} md={6} xs={12} flexDirection={'column'}>
                <Autocomplete
                    multiple
                    options={assigneeOptions}
                    disabled={assignToAll}
                    value={removalAssigneeList ?? []}
                    slotProps={{
                        popper: {
                        sx: {
                            zIndex: 10000
                        }
                        }
                    }}
                    onChange={(event, newValue) => {
                        setRemovalAssigneeList(newValue.length ? newValue : null);
                    }}
                    clearOnEscape
                    filterSelectedOptions
                    renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Removal Assignees"
                        helperText= { removalGroup && removalGroup!=="N/A" && !assignToAll && !removalAssigneeList ? "Add a removal assignee from the selected removal group": ''}
                    />
                    )}
                    fullWidth
                    limitTags={1}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip label={option} {...getTagProps({ index })} />
                        ))
                    }
                />
                <FormControlLabel
                    control={
                        <Switch
                            disabled={!removalGroup || !assigneeOptions}
                            checked={assignToAll}
                            onChange={(e) => {
                                const checked = e.target.checked
                                setAssignToAll(checked)
                            }}
                        />
                    }
                    label="Assign to all users in the group"
                />
                <br/>
                <Divider sx={{ my: 4 }} />
                <FormControlLabel
                    control={
                        <Switch
                            checked={holdBrokerStatus}
                            onChange={(e) => {
                                setHoldBrokerStatus(e.target.checked);
                            }}
                        />
                    }
                    label="Hold Broker Status"
                />

            </Grid>
          <Grid item lg={6} md={6} xs={12}>
            <Autocomplete
                options={removalRolesOptions}
                value={removalGroup? getRemovalRoleKeyFromValue(removalGroup) : null}
                slotProps={{
                    popper: {
                    sx: {
                        zIndex: 10000
                    }
                    }
                }}
                onChange={(event, newValue) => {
                    if (!newValue) {
                        setRemovalGroup(null)
                    } else {
                        setRemovalGroup(removalRoleMapping[newValue as keyof typeof removalRoleMapping])
                    }
                    setAssignToAll(false)
                    setRemovalAssigneeList(null)
                }}
                clearOnEscape
                renderInput={(params) => (
                <TextField
                    {...params}
                    label="Removal Group"
                />
                )}
                fullWidth
                isOptionEqualToValue={(option, value) => option === value}
            />
        </Grid>
        </Grid>
        <Grid
            container
            width="100%"
            justifyContent="flex-end"
            spacing={2}
            >
            <Grid item>
                <Button
                variant="outlined"
                onClick={() => onClose()}
                sx={{
                    borderRadius: "10px",
                    height: "45px",
                    minWidth: "120px"
                }}
                >
                Close
                </Button>
            </Grid>

            <Grid item>
                <Button
                variant="contained"
                disabled={!hasChanges}
                onClick={() => {
                    const finalAssignees = assignToAll
                    ? assigneeOptions
                    : removalAssigneeList
                    props.configUpdate({
                        brokerName: props.broker.name,
                        brokerId: props.broker.id,
                        removalAssigneeList: finalAssignees,
                        removalGroup: removalGroup && removalGroup==="N/A"? null: removalGroup,
                        is_removal_on_hold: holdBrokerStatus
                    })
                    onClose()
                }}
                sx={{
                    borderRadius: "10px",
                    height: "45px",
                    minWidth: "120px"
                }}
                >
                Save Changes
                </Button>
            </Grid>
            </Grid>

      </Grid>
    </Modal>
  )
}

export default ConfirgureRemovalBrokersModal